import React, { useEffect, useState, useRef, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Gift } from "lucide-react";
import { setProducts } from "../../server/Product.js";
import {
  getAllCategory,
  getBySlug,
  setCurrentCategoryDispatch,
} from "../../server/Category";
import SubNavbar from "./SubNavbar.js";
import SmartScrollRow from "./SmartScrollRow.js";
import { reloedFunc } from "../../server/Product.js";
import { getCategoryNavIconComponent } from "./categoryNavIcons.js";

const formatCategoryName = (name) => {
  const lines = name.split("|");

  return (
    <span style={{ lineHeight: "1.2" }}>
      {lines.map((line, index) => (
        <span className="nowrap-text" key={index}>
          {line.trim()}
          {index < lines.length - 1 && <br />}
        </span>
      ))}
    </span>
  );
};

const NAVIGATOR = ({ currentSubCategory, setPendingScrollSlug }) => {
  const {
    categories,
    toggleMenu,
    stationId,
    items,
    isClientView,
    includeInactive,
    role,
  } = useSelector((state) => ({
    categories: state.category.categories,
    toggleMenu: state.openCart.toggleMenu,
    stationId: state.user.stationId,
    items: state.displayProduct.itemsShow,
    isClientView: state.category.isClientView,
    includeInactive: state.category.includeInactive,
    role: state.user.role,
  }));
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [children, setChildren] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [, setActiveSlug] = useState(null);

  const currentAbortController = useRef(null);
  const isSalesPageRef = useRef(false);

  const isClientViewRef = useRef(isClientView);
  const includeInactiveRef = useRef(includeInactive);

  const handleCategoryClick = useCallback(
    (slug) => {
      setActiveSlug(slug);
      dispatch(setProducts([]));
      dispatch(reloedFunc());
      if (currentAbortController.current) {
        currentAbortController.current.abort();
      }

      const abortController = new AbortController();
      currentAbortController.current = abortController;
      setChildren(null);

      getBySlug(
        stationId,
        slug,
        isClientViewRef.current,
        includeInactiveRef.current,
        {
          signal: abortController.signal,
        },
      )
        .then((res) => {
          if (isSalesPageRef.current) return;

          if (!abortController.signal.aborted) {
            dispatch(setProducts(res));
          }
        })
        .catch((err) => {
          if (err.name !== "AbortError") console.error(err);
        });
    },
    [dispatch, stationId],
  );

  useEffect(() => {
    isSalesPageRef.current = pathname === "/sales";
  }, [pathname]);

  useEffect(() => {
    if (pathname === "/sales" && currentAbortController.current) {
      currentAbortController.current.abort();
    }
  }, [pathname]);

  useEffect(() => {
    if (localStorage.getItem("jwtToken"))
      dispatch(getAllCategory(stationId, null, 5, true, false));
  }, [dispatch, stationId]);

  useEffect(() => {
    if (pathname.startsWith("/category/")) {
      const splited = pathname.split("/");
      const slug = splited[2];
      setCurrentCategory(slug);
      if (
        currentCategory !== slug &&
        items?.slug !== slug &&
        splited.length === 3
      ) {
        handleCategoryClick(slug);
      }
      const category = categories?.find((cat) => cat.slug === slug);
      setChildren(category);
      dispatch(setCurrentCategoryDispatch(category));
    } else {
      setCurrentCategory(null);
      setChildren(null);
    }
  }, [
    pathname,
    categories,
    currentCategory,
    items?.slug,
    handleCategoryClick,
    dispatch,
  ]);

  useEffect(() => {
    if (pathname.startsWith("/category/")) {
      const splited = pathname.split("/");
      const slug = splited[2];
      if (splited.length === 3) {
        handleCategoryClick(slug);
        setCurrentCategory(slug);
      }
    }
    // Re-fetch when station changes; full deps would duplicate the category effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  useEffect(() => {
    if (role) isClientViewRef.current = isClientView;
    else isClientViewRef.current = true;
  }, [isClientView, role]);

  useEffect(() => {
    if (role) includeInactiveRef.current = includeInactive;
    else includeInactiveRef.current = false;
  }, [includeInactive, role]);

  return (
    <div className="navigator">
      <div className="navigator__stack">
        <nav className={toggleMenu ? "active main" : "main"}>
          <SmartScrollRow rtl trackClassName="navigator__scroll-track">
            <div className="item-sale">
              <NavLink
                className={
                  pathname === "/sales"
                    ? "item-sale-link item-sale-link--active"
                    : "item-sale-link"
                }
                to="/sales"
              >
                <span className="item-sale-icon-wrap" aria-hidden>
                  <Gift
                    className="item-sale-icon-svg"
                    size={26}
                    strokeWidth={2}
                  />
                </span>
                <span className="item-sale-label">מבצעים</span>
              </NavLink>
            </div>
            {categories &&
              categories.length > 0 &&
              categories
                .filter(
                  (cat) =>
                    cat.status === "ACTIVE" &&
                    cat.totalProducts > 0 &&
                    (!includeInactive || !role ? cat.activeProducts > 0 : true),
                )
                .map((element, index) => {
                  const CategoryIcon = getCategoryNavIconComponent(
                    element.slug,
                    element.name,
                    index,
                  );
                  return (
                    <a
                      key={element.slug || index}
                      href="/"
                      className={
                        element.slug === currentCategory
                          ? "item-active"
                          : "item"
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/category/${element.slug}`);
                      }}
                    >
                      <span className="category-nav-item-inner">
                        <span className="category-nav-item-icon" aria-hidden>
                          <CategoryIcon
                            className="category-nav-svg"
                            size={22}
                            strokeWidth={1.65}
                            aria-hidden
                          />
                        </span>
                        <span className="category-nav-item-label">
                          {formatCategoryName(element.name)}
                        </span>
                      </span>
                    </a>
                  );
                })}
          </SmartScrollRow>
        </nav>
        {children?.children && (
          <SubNavbar
            fatherSlug={currentCategory}
            currentSubCategory={currentSubCategory}
            currentCategories={children}
            setPendingScrollSlug={setPendingScrollSlug}
          />
        )}
      </div>
    </div>
  );
};

export default NAVIGATOR;
